import React, { useState, useEffect } from "react";
import {
  Container, Typography, Box, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, IconButton, Alert, Snackbar,
  MenuItem, FormControl, InputLabel, Select, Divider, Chip,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { collection, query, onSnapshot, deleteDoc, doc, where, or } from "firebase/firestore";
import { firestore } from "../../../firebase";
import { createOrUpdateTenantAndAdmin } from "../../DatabaseAPI/Api";

const TENANT_TYPES = [
  { value: "BUSINESS_VENDOR", label: "Business Vendor" },
  { value: "ORGANISATION", label: "Organisation" },
];
const TENANT_STATUSES = ["ACTIVE", "INACTIVE", "SUSPENDED"];
const INSTITUTION_CATEGORIES = ["SCHOOL", "HOSPITAL", "COLLEGE", "OTHER"];

const normalizeTenantType = (type) => {
  if (!type || type === "-") return "-";
  if (type === "BUSINESS") return "BUSINESS_VENDOR";
  if (type === "INSTITUTIONAL") return "ORGANISATION";
  return type;
};

const formatTenantType = (type) => {
  const normalized = normalizeTenantType(type);
  if (normalized === "BUSINESS_VENDOR") return "Business Vendor";
  if (normalized === "ORGANISATION") return "Organisation";
  return normalized;
};

const DEFAULT_CONFIGURATION = {
  warrantyEnabled: true,
  complaintEnabled: true,
  contractEnabled: true,
  checklistEnabled: true,
  scheduleEnabled: true,
};

const EMPTY_FORM = {
  tenantName: "", tenantId: "", tenantType: "BUSINESS_VENDOR", status: "ACTIVE",
  adminName: "", email: "", phoneNumber: "",
  companyRegistrationNumber: "", gstNumber: "", headOfficeAddress: "",
  contactEmail: "", contactPhone: "",
  organisationName: "", organisationUrl: "", organisationLogoUrl: "",
  institutionName: "", institutionUrl: "", institutionLogoUrl: "",
  institutionCategory: "SCHOOL", affiliation: "", institutionAddress: "",
  customInstitutionCategory: "",
  institutionContactEmail: "",
  configuration: { ...DEFAULT_CONFIGURATION },
};

const SuperAdminPage = ({ currentUser }) => {
  const [admins, setAdmins] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [adminData, setAdminData] = useState({ ...EMPTY_FORM, configuration: { ...DEFAULT_CONFIGURATION } });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (!currentUser?.uid) return;
    const userQuery = query(
      collection(firestore, "UserProfileMaster"),
      or(where("createdBy", "==", currentUser.uid), where("uid", "==", currentUser.uid))
    );
    const tenantQuery = query(collection(firestore, "TenantMaster"), where("createdBy", "==", currentUser.uid));
    const unsubUsers = onSnapshot(userQuery, (snap) => {
      setAdmins(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => ["admin", "organisation_admin", "superadmin"].includes(u.role))
      );
    });
    const unsubTenants = onSnapshot(tenantQuery, (snap) => {
      setTenants(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => { unsubUsers(); unsubTenants(); };
  }, [currentUser]);

  const mergedAdmins = admins.map((admin) => {
    const t = tenants.find((t) => t.adminEmail === admin.email) || tenants.find((t) => t.tenantId === admin.tenantId);
    return {
      ...admin,
      tenantId: t?.tenantId || admin.tenantId || "-",
      tenantName: t?.tenantName || admin.tenantName || "-",
      tenantType: normalizeTenantType(t?.tenantType || admin.tenantType || "-"),
      status: t?.status || "-",
      adminName: t?.adminName || admin.adminName || "-",
    };
  });

  const handleOpenDialog = (admin = null) => {
    if (admin) {
      const t = tenants.find((t) => t.adminEmail === admin.email || t.tenantId === admin.tenantId);
      setEditingAdmin(admin);
      setAdminData({
        tenantName: t?.tenantName || admin.tenantName || "",
        tenantId: t?.tenantId || admin.tenantId || "",
        tenantType: normalizeTenantType(t?.tenantType || admin.tenantType || "BUSINESS_VENDOR"),
        status: t?.status || "ACTIVE",
        adminName: t?.adminName || admin.adminName || "",
        email: admin.email || "",
        phoneNumber: admin.phoneNumber || "",
        companyRegistrationNumber: t?.companyRegistrationNumber || "",
        gstNumber: t?.gstNumber || "",
        headOfficeAddress: t?.headOfficeAddress || "",
        contactEmail: t?.contactEmail || "",
        contactPhone: t?.contactPhone || "",
        organisationName: t?.organisationName || "",
        organisationUrl: t?.organisationUrl || "",
        organisationLogoUrl: t?.organisationLogoUrl || "",
        institutionName: t?.institutionName || "",
        institutionUrl: t?.institutionUrl || "",
        institutionLogoUrl: t?.institutionLogoUrl || "",
        institutionCategory: t?.institutionCategory || "SCHOOL",
        customInstitutionCategory: t?.customInstitutionCategory || "",
        affiliation: t?.affiliation || "",
        institutionAddress: t?.institutionAddress || "",
        institutionContactEmail: t?.institutionContactEmail || "",
        configuration: t?.configuration ? { ...DEFAULT_CONFIGURATION, ...t.configuration } : { ...DEFAULT_CONFIGURATION },
      });
    } else {
      setEditingAdmin(null);
      setAdminData({ ...EMPTY_FORM, configuration: { ...DEFAULT_CONFIGURATION } });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAdmin(null);
    setAdminData({ ...EMPTY_FORM, configuration: { ...DEFAULT_CONFIGURATION } });
  };

  const handleFieldChange = (field) => (e) => setAdminData((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSaveAdmin = async () => {
    if (!adminData?.tenantName?.trim() || !adminData?.adminName?.trim() || !adminData?.email?.trim() || !adminData?.tenantType) {
      setSnackbar({ open: true, message: "Tenant Name, Tenant Type, Admin Name, and Email are required.", severity: "error" });
      return;
    }

    if (adminData.tenantType === "BUSINESS_VENDOR") {
      if (
        !adminData?.companyRegistrationNumber?.trim() ||
        !adminData?.gstNumber?.trim() ||
        !adminData?.headOfficeAddress?.trim() ||
        !adminData?.contactEmail?.trim() ||
        !adminData?.contactPhone?.trim()
      ) {
        setSnackbar({ open: true, message: "All Business Vendor details are mandatory.", severity: "error" });
        return;
      }
    }

    if (adminData.tenantType === "ORGANISATION") {
      const selectedCategory = adminData?.institutionCategory;
      const isOtherCategory = selectedCategory === "OTHER";
      if (
        !adminData?.organisationName?.trim() ||
        !adminData?.organisationUrl?.trim() ||
        !adminData?.organisationLogoUrl?.trim() ||
        !selectedCategory ||
        (isOtherCategory && !adminData?.customInstitutionCategory?.trim()) ||
        !adminData?.affiliation?.trim() ||
        !adminData?.institutionAddress?.trim() ||
        !adminData?.institutionContactEmail?.trim()
      ) {
        setSnackbar({ open: true, message: "All Organisation details are mandatory.", severity: "error" });
        return;
      }
    }

    if (!currentUser?.uid) {
      setSnackbar({ open: true, message: "Error: You are not logged in.", severity: "error" });
      return;
    }
    try {
      const result = await createOrUpdateTenantAndAdmin(adminData, editingAdmin ? editingAdmin.id : null, currentUser.uid);
      setSnackbar({ open: true, message: result.message || (result.success ? "Saved successfully" : "Error saving"), severity: result.success ? "success" : "error" });
      if (result.success) handleCloseDialog();
    } catch (error) {
      setSnackbar({ open: true, message: "Error saving tenant/admin", severity: "error" });
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (window.confirm("Are you sure you want to delete this admin?")) {
      try {
        await deleteDoc(doc(firestore, "UserProfileMaster", adminId));
        setSnackbar({ open: true, message: "Admin deleted successfully", severity: "success" });
      } catch {
        setSnackbar({ open: true, message: "Error deleting admin", severity: "error" });
      }
    }
  };

  const statusColor = { ACTIVE: "success", INACTIVE: "default", SUSPENDED: "error" };

  return (
    <Container>
      <Box sx={{ my: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h4">Tenant Management</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()} sx={{ backgroundColor: "#1976d2" }}>
          Add Tenant
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {["Tenant ID", "Tenant Name", "Type", "Status", "Admin Name", "Admin Email", "Admin Mobile", "Actions"].map((h) => (
                <TableCell key={h} sx={{ fontWeight: "bold" }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {mergedAdmins.map((admin) => (
              <TableRow key={admin.id}>
                <TableCell>{admin.tenantId}</TableCell>
                <TableCell>{admin.tenantName}</TableCell>
                <TableCell>{formatTenantType(admin.tenantType)}</TableCell>
                <TableCell>
                  <Chip label={admin.status} color={statusColor[admin.status] || "default"} size="small" />
                </TableCell>
                <TableCell>{admin.adminName}</TableCell>
                <TableCell>{admin.email}</TableCell>
                <TableCell>{admin.phoneNumber || "-"}</TableCell>
                <TableCell>
                  {admin.role !== "superadmin" && (
                    <>
                      <IconButton color="primary" onClick={() => handleOpenDialog(admin)}><Edit /></IconButton>
                      <IconButton color="error" onClick={() => handleDeleteAdmin(admin.id)}><Delete /></IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingAdmin ? "Edit Tenant" : "Add New Tenant"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>

            {/* Section 1: Core */}
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>Core Tenant Information</Typography>
            {editingAdmin && (
              <TextField fullWidth label="Tenant ID" value={adminData.tenantId}
                InputProps={{ readOnly: true, sx: { color: "text.secondary" } }}
                helperText="Auto-generated Tenant ID (read-only)" />
            )}
            <TextField required fullWidth label="Tenant Name" value={adminData.tenantName} onChange={handleFieldChange("tenantName")} />
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl fullWidth required>
                <InputLabel>Tenant Type</InputLabel>
                <Select value={adminData.tenantType} label="Tenant Type" onChange={handleFieldChange("tenantType")}>
                  {TENANT_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>Status</InputLabel>
                <Select value={adminData.status} label="Status" onChange={handleFieldChange("status")}>
                  {TENANT_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>

            <Divider />

            {/* Section 2: Admin Assignment */}
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>Tenant Admin Assignment</Typography>
            <TextField required fullWidth label="Admin Name" value={adminData.adminName} onChange={handleFieldChange("adminName")} />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField required fullWidth label="Admin Email" value={adminData.email} onChange={handleFieldChange("email")} />
              <TextField fullWidth label="Admin Mobile No (optional)" value={adminData.phoneNumber} onChange={handleFieldChange("phoneNumber")} />
            </Box>

            <Divider />

            {/* Section 3: Business-Specific */}
            {adminData.tenantType === "BUSINESS_VENDOR" && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>Business Vendor Details</Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField required fullWidth label="Company Registration Number" value={adminData.companyRegistrationNumber} onChange={handleFieldChange("companyRegistrationNumber")} />
                  <TextField required fullWidth label="GST Number" value={adminData.gstNumber} onChange={handleFieldChange("gstNumber")} />
                </Box>
                <TextField required fullWidth label="Head Office Address" value={adminData.headOfficeAddress} onChange={handleFieldChange("headOfficeAddress")} />
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField required fullWidth label="Business Vendor Contact Email" value={adminData.contactEmail} onChange={handleFieldChange("contactEmail")} />
                  <TextField required fullWidth label="Business Vendor Contact Phone" value={adminData.contactPhone} onChange={handleFieldChange("contactPhone")} />
                </Box>
                <Divider />
              </>
            )}

            {/* Section 4: Institutional-Specific */}
            {adminData.tenantType === "ORGANISATION" && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>Organisation Details</Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField required fullWidth label="Organisation Name" value={adminData.organisationName} onChange={handleFieldChange("organisationName")} />
                  <TextField required fullWidth label="Organisation URL" value={adminData.organisationUrl} onChange={handleFieldChange("organisationUrl")} />
                </Box>
                <TextField required fullWidth label="Organisation Logo URL" value={adminData.organisationLogoUrl} onChange={handleFieldChange("organisationLogoUrl")} />
                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Organisation Category</InputLabel>
                    <Select value={adminData.institutionCategory} label="Organisation Category" onChange={handleFieldChange("institutionCategory")}>
                      {INSTITUTION_CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField required fullWidth label="Affiliation / Board / Authority" value={adminData.affiliation} onChange={handleFieldChange("affiliation")} />
                </Box>
                {adminData.institutionCategory === "OTHER" && (
                  <TextField
                    required
                    fullWidth
                    label="Custom Organisation Category"
                    value={adminData.customInstitutionCategory}
                    onChange={handleFieldChange("customInstitutionCategory")}
                    helperText="Write your organisation category"
                  />
                )}
                <TextField required fullWidth label="Organisation Address" value={adminData.institutionAddress} onChange={handleFieldChange("institutionAddress")} />
                <TextField required fullWidth label="Organisation Contact Email" value={adminData.institutionContactEmail} onChange={handleFieldChange("institutionContactEmail")} />
                <Divider />
              </>
            )}

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveAdmin} variant="contained" color={editingAdmin ? "primary" : "success"}>
            {editingAdmin ? "Update" : "Add Tenant"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SuperAdminPage;